import { FormSectionHandler } from '../entity.handler';
import { OpenIdConnectIssuer, OpenIdConnectIssuers } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { EntityUtils, TextField } from '../utils';
import { ListFieldValue } from 'app/shared/components/form-builder/list/list.component';
import * as uuid from 'uuid';

interface Issuer extends OpenIdConnectIssuer {
    id: string;
}

interface EditorValues {
    items?: ListFieldValue<Issuer>[];
}


export class OidcIssuersSectionHandler<T extends { id: string, oidc?: OpenIdConnectIssuers }> implements FormSectionHandler<T, EditorValues> {

    type = 'form';
    icon = 'key';
    bulk = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    removedItems = [];

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        public entityType: string
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.SHARED.OIDC_ISSUERS.${key}`, values);
    }

    applyUpdate(entity: T, payload: any): Promise<any> {
        switch (this.entityType) {
            case 'client': return this.admin.applyUpdate(api => api.accounts.updateClientDetails(entity.id, payload));
            case 'vendor': return this.admin.applyUpdate(api => api.accounts.updateVendorDetails(entity.id, payload));
            case 'distributor': return this.admin.applyUpdate(api => api.accounts.updateDistributorDetails(entity.id, payload));
            default:
                throw new Error(`Update for '${this.entityType}' not supported`);
        }
    }

    async mapEntityToEditor(entity: T): Promise<EditorValues> {
        this.removedItems = [];
        return {
            items: Object.keys(entity.oidc).map(key => this.stateItemToListItem({ id: key, ...entity.oidc[key] })),
        };
    }

    mapEditorToEntity(entity: T, values: EditorValues): Promise<T> {

        entity.oidc = values.items.filter(x => x.modified).reduce((obj, item) => {
            const { id, ...rest } = item.data;
            obj[id] = rest;
            return obj;
        }, {} as OpenIdConnectIssuers);
        this.removedItems.forEach(id => {
            entity.oidc[id] = null;
        });
        return Promise.resolve(entity);
    }

    getFields(entity: T): Promise<FormBuilderField[]> {
        return Promise.resolve().then(() => {
            return [
                {
                    id: 'items', title: this.translate('FIELDS.ISSUERS'), type: 'list', required: false,
                    actions: [
                        { name: this.admin.i18n.instant('SHARED.ADD'), click: this.showFilterModal.bind(this), },
                        { id: 'edit', click: this.showFilterModal.bind(this), },
                        {
                            id: 'delete', click: async (_action, _field, _items, item) => {
                                this.removedItems.push(item.id);
                            },
                         },
                    ],
                    getText: (field: FormBuilderField, values: any) => values[field.id] && values[field.id].map(x => x.name).join(', '),
                },
            ];
        }).then(fields => EntityUtils.prepareBulk(!entity, [], fields));

    }

    stateItemToListItem(item: Issuer, modified?: boolean): ListFieldValue<Issuer> {
        return {
            id: item.id,
            name: item.name,
            icon: 'key',
            data: item,
            modified: modified,
        };
    }

    updateOrAddItem(items: ListFieldValue<Issuer>[], existingItem: ListFieldValue<Issuer>, item: Issuer) {
        if (existingItem) {
            const idx = items.findIndex(x => x.id === existingItem.id);
            items[idx] = this.stateItemToListItem(item, true);
        } else {
            items.push(this.stateItemToListItem(item, true));
        }
    }

    showFilterModal(_action: string, _field: FormBuilderField, values: EditorValues, item: ListFieldValue<any>): Promise<Issuer> {

        const data = (item && item.data)
            ? { ...item.data, clientSecret: '********' }
            : { id: uuid.v4(), };

        return this.admin.showFormModal<Issuer>({
            groups: [{
                name: this.translate('MODAL.TITLE'),
                description: this.translate('MODAL.DESC'),
                fields: [
                    { ...TextField('name', this.translate('FIELDS.NAME'), true, 1, 1024), description: this.translate('FIELDS.NAME_DESC') },
                    { ...TextField('issuer', this.translate('FIELDS.ISSUER'), true, 1, 1024), description: this.translate('FIELDS.ISSUER_DESC') },
                    { ...TextField('clientId', this.translate('FIELDS.CLIENT_ID'), true, 1, 1024), description: this.translate('FIELDS.CLIENT_ID_DESC') },
                    { ...TextField('clientSecret', this.translate('FIELDS.CLIENT_SECRET'), false, 1, 1024), type: 'password', description: this.translate('FIELDS.CLIENT_SECRET_DESC') },
                ],
            }],
        }, data).then(result => {
            if (result) {
                this.updateOrAddItem(values.items, item, {
                    ...result,
                    // tslint:disable-next-line: tsr-detect-possible-timing-attacks
                    clientSecret: result.clientSecret === '********' ? undefined : result.clientSecret,
                });
            }
            return result;
        });

    }


}
